import { useNavigate } from 'react-router';
import { useGetProgramThemes, type ProgramTheme } from '../api/useProgramThemes';
import { DataTable, type Column } from './DataTable';
import { Pagination } from './Pagination';
import { SearchInput } from './SearchInput';
import { ThemeStatusBadge } from './ThemeStatusBadge';
import { useUrlSearch } from '../hooks/useUrlSearch';
import { useFittedPageSize } from '../hooks/useFittedPageSize';
import { formatDate } from '../lib/date';

const Swatch = ({ color, label }: { color: string; label: string }) => (
  <span
    title={`${label} #${color.toUpperCase()}`}
    className="h-4 w-4 rounded border border-gray-200"
    style={{ backgroundColor: `#${color}` }}
  />
);

const columns: Column<ProgramTheme>[] = [
  {
    key: 'name',
    header: 'Theme',
    cell: (t) => (
      <span className="inline-flex items-center gap-3">
        <img
          src={t.strip}
          alt=""
          loading="lazy"
          className={`h-5 w-14 shrink-0 rounded object-cover bg-gray-100 ${t.archived ? 'opacity-50 grayscale' : ''}`}
        />
        <span className={t.archived ? 'text-gray-400' : 'text-gray-900'}>{t.name}</span>
      </span>
    ),
  },
  { key: 'status', header: 'Status', cell: (t) => <ThemeStatusBadge theme={t} /> },
  {
    key: 'colours',
    header: 'Colours',
    cell: (t) => (
      <span className="inline-flex items-center gap-1">
        <Swatch color={t.background_color} label="Background" />
        <Swatch color={t.label_color} label="Label" />
        <Swatch color={t.text_color} label="Text" />
      </span>
    ),
  },
  { key: 'updated', header: 'Updated', className: 'text-gray-500', cell: (t) => formatDate(t.updated_at) },
  { key: 'uid', header: 'UID', className: 'font-mono text-xs text-gray-400', cell: (t) => `${t.uid.slice(0, 8)}…`, copyValue: (t) => t.uid },
];

export const ThemesTable = () => {
  const navigate = useNavigate();
  const { query, page, search, setSearch, goToPage } = useUrlSearch();
  const { tableRef, pageSize } = useFittedPageSize();
  const { data, isLoading, isError } = useGetProgramThemes({ page, limit: pageSize, q: query || undefined });

  const themes = data?.payload?.themes ?? [];
  const total = data?.payload?.total ?? 0;
  const showPagination = !isLoading && !isError && themes.length > 0;
  const emptyLabel = query
    ? 'No themes match that name.'
    : 'No themes yet. New programs start on the default theme, and the first theme you create becomes the default.';

  return (
    <div className="flex flex-col gap-4">
      <SearchInput value={search} onChange={setSearch} placeholder="Search by name" label="Search themes" />
      <div ref={tableRef}>
        <DataTable
          columns={columns}
          rows={themes}
          rowKey={(t) => t.uid}
          onRowClick={(t) => navigate(`/themes/${t.uid}`)}
          isLoading={isLoading}
          isError={isError}
          errorLabel="Failed to load themes."
          emptyLabel={emptyLabel}
        />
      </div>
      {showPagination && <Pagination page={page} pageSize={pageSize} total={total} onPageChange={goToPage} />}
    </div>
  );
};
